/**
 * CANONICAL JSON — the only text `@ff/store` ever writes to a record.
 *
 * Every write in this package is verified by re-reading it and comparing
 * strings (`verifyWrite`), and journal entries are content-hashed. Both of those
 * are only meaningful over a *deterministic* encoding, and `JSON.stringify` is
 * not one: it emits object keys in insertion order, so two structurally
 * identical values encode differently and verification starts failing at random
 * on values nothing is wrong with. Once that happens the honest failure — real
 * corruption — is indistinguishable from noise, and the fail-closed contract
 * stops meaning anything.
 *
 * Three rules, all of them from spec F-JE-02 (`sortedKeys` +
 * `withoutEscapingSlashes`, dates as a single Int64 of epoch microseconds
 * rounded away from zero), reproduced here because the bytes this file writes
 * have to match the bytes the native encoder wrote:
 *
 *  1. **Object keys are sorted.** Ascending by UTF-16 code unit. Record keys in
 *     this codebase are ASCII identifiers, where code-unit order and the UTF-8
 *     byte order Swift's `sortedKeys` uses are the same order.
 *  2. **Slashes are not escaped.** `JSON.stringify` already leaves `/` alone,
 *     which is exactly what `withoutEscapingSlashes` buys on the Swift side —
 *     but it is asserted by a test rather than assumed, because a hand-rolled
 *     string escaper added later would be the obvious place to reintroduce
 *     `\/`.
 *  3. **A `Date` is one integer of epoch microseconds**, rounded half away from
 *     zero, and out of range it throws rather than truncating.
 *
 * The encoder is *strict about what it accepts* rather than lenient like
 * `JSON.stringify`. Silently dropping a function-valued property or turning
 * `NaN` into `null` produces text that no longer describes the value it was
 * given, and the first place that difference surfaces is a verification failure
 * on a write nobody can explain. Refusing at the encoder names the real cause.
 */

import { journalInstantFromMilliseconds } from "@ff/domain";

/**
 * A value this encoder refuses to represent.
 *
 * Carries the path to the offending node because the whole point of refusing is
 * to name the field, and an envelope is nested deeply enough that "unsupported
 * value" alone would send the reader hunting.
 */
export class CanonicalJsonError extends Error {
  readonly path: string;

  constructor(message: string, path: string) {
    super(`${message} at ${path}`);
    this.name = "CanonicalJsonError";
    this.path = path;
  }
}

/**
 * Epoch milliseconds to the integer of epoch microseconds this encoding writes.
 *
 * Delegates to `@ff/domain`'s `journalInstantFromMilliseconds` rather than
 * repeating the arithmetic: the rounding rule (half **away from zero**, not
 * JavaScript's `Math.round`, which rounds −0.5 to −0) and the representable
 * range are contract, and a second implementation of a rounding rule is a
 * second implementation to get subtly wrong. Out of range it throws the exact
 * `RangeError` F-JE-02 names.
 */
export function canonicalEpochMicroseconds(epochMilliseconds: number): number {
  return journalInstantFromMilliseconds(epochMilliseconds);
}

/** Deterministic JSON text for `value`. */
export function canonicalJson(value: unknown): string {
  const parts: string[] = [];
  encodeValue(value, "$", parts, new Set<object>());
  return parts.join("");
}

function encodeValue(
  value: unknown,
  path: string,
  out: string[],
  ancestors: Set<object>,
): void {
  if (value === null) {
    out.push("null");
    return;
  }

  switch (typeof value) {
    case "boolean":
      out.push(value ? "true" : "false");
      return;
    case "number":
      // `NaN` and the infinities have no JSON form. `JSON.stringify` writes
      // `null` for them, which reads back as a different value than was
      // written — the one thing an encoding used for verification may not do.
      if (!Number.isFinite(value)) {
        throw new CanonicalJsonError(`non-finite number ${String(value)}`, path);
      }
      // `JSON.stringify` of a finite number is the shortest round-tripping
      // decimal form and normalizes `-0` to `0`, so it is already canonical.
      out.push(JSON.stringify(value));
      return;
    case "string":
      // Well-formed `JSON.stringify` escapes lone surrogates as `\uXXXX` and
      // leaves `/` alone; both are deterministic and both match the Swift
      // encoder configured `withoutEscapingSlashes`.
      out.push(JSON.stringify(value));
      return;
    case "undefined":
      // Reachable only at the root or inside an array; object properties are
      // filtered before recursion. Neither has a form that survives a round
      // trip as `undefined`, so the caller is told instead of guessed at.
      throw new CanonicalJsonError("undefined has no canonical form", path);
    case "bigint":
      // A BigInt beyond 2^53 cannot be re-read as the same JavaScript number,
      // so encoding it would produce text that decodes to a different value.
      throw new CanonicalJsonError("bigint has no canonical form", path);
    default:
      break;
  }

  if (typeof value === "function" || typeof value === "symbol") {
    throw new CanonicalJsonError(`${typeof value} has no canonical form`, path);
  }

  const object = value as object;
  if (ancestors.has(object)) {
    throw new CanonicalJsonError("circular reference", path);
  }

  if (value instanceof Date) {
    // An invalid `Date` has a `NaN` time, which the domain's range check
    // rejects with the same `RangeError` an out-of-range date gets — both are
    // "this instant cannot be written", and both must stop the write.
    out.push(String(canonicalEpochMicroseconds(value.getTime())));
    return;
  }

  ancestors.add(object);
  try {
    if (Array.isArray(value)) {
      encodeArray(value, path, out, ancestors);
      return;
    }
    const prototype = Object.getPrototypeOf(object) as unknown;
    if (prototype !== Object.prototype && prototype !== null) {
      // Maps, Sets, typed arrays and class instances all have a shape that
      // `JSON.stringify` would flatten into something that no longer decodes
      // back to what it was given. Records in this codebase are plain data.
      throw new CanonicalJsonError(
        `values of type ${constructorName(object)} have no canonical form`,
        path,
      );
    }
    encodeObject(object as Record<string, unknown>, path, out, ancestors);
  } finally {
    ancestors.delete(object);
  }
}

function encodeArray(
  value: readonly unknown[],
  path: string,
  out: string[],
  ancestors: Set<object>,
): void {
  out.push("[");
  for (let index = 0; index < value.length; index += 1) {
    if (index > 0) out.push(",");
    const element = value[index];
    const elementPath = `${path}[${index}]`;
    // A hole in a sparse array and an explicit `undefined` are the same thing
    // once encoded, and both decode back as `null`. Writing `null` is therefore
    // stable across a round trip, which is the property verification needs; the
    // refusal above is only for values that would *change*.
    if (element === undefined) {
      out.push("null");
      continue;
    }
    encodeValue(element, elementPath, out, ancestors);
  }
  out.push("]");
}

function encodeObject(
  value: Record<string, unknown>,
  path: string,
  out: string[],
  ancestors: Set<object>,
): void {
  // Sorted, always. This single line is what makes the encoding canonical, and
  // everything else in this package that compares strings depends on it.
  const keys = Object.keys(value).sort();
  out.push("{");
  let written = 0;
  for (const key of keys) {
    const entry = value[key];
    // Absent and `undefined` must encode identically: an optional field
    // explicitly set to `undefined` in memory comes back absent after a round
    // trip, and if the two encoded differently every such record would fail its
    // own write verification.
    if (entry === undefined) continue;
    if (written > 0) out.push(",");
    out.push(JSON.stringify(key), ":");
    encodeValue(entry, `${path}.${key}`, out, ancestors);
    written += 1;
  }
  out.push("}");
}

function constructorName(value: object): string {
  const named = value.constructor as { name?: string } | undefined;
  return named?.name ?? "object";
}
